import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from 'hooks/auth'
import { hasSeenRelease, markReleaseSeen } from './releaseHistory'
import { STATUS } from 'react-joyride'
import { ArrowRight, ChevronLeft, ChevronRight, Maximize2, X } from 'lucide-react'
import { Button, Modal } from '@/components/ds2'
import { ReleaseFeature, releaseFeatures } from './releases'
import { ReleaseTour } from './ReleaseTour'
import { PageFeatureTour } from './PageFeatureTour'
import { useReleaseSwipe } from './useReleaseSwipe'

export const FeatureRelease = () => {
    const { user, application } = useAuth()
    const historyScope = user?.id && application?.projectName ? JSON.stringify([application.projectName, user.id]) : null
    if (!historyScope) return null
    return <FeatureReleaseContent key={historyScope} historyScope={historyScope} />
}

const FeatureReleaseContent = ({ historyScope }: { historyScope: string }) => {
    const [open, setOpen] = useState(false)
    const [showPreview, setShowPreview] = useState(true)
    const [pageTourActive, setPageTourActive] = useState(false)
    const handlePageTourActiveChange = useCallback((active: boolean) => {
        setPageTourActive(active)
        if (active) setShowPreview(false)
    }, [])
    const [index, setIndex] = useState(0)
    const [pending, setPending] = useState<ReleaseFeature | null>(null)
    const [ready, setReady] = useState(false)
    const [unavailable, setUnavailable] = useState(false)
    const { pathname } = useLocation()
    const previousPathname = useRef(pathname)
    const navigate = useNavigate()
    const announcements = releaseFeatures.filter((item) => item.route === pathname && historyScope && !hasSeenRelease(historyScope, item.id))
    const feature = announcements[index] ?? announcements[0]
    const swipeHandlers = useReleaseSwipe((direction) => {
        setIndex((current) => Math.max(0, Math.min(announcements.length - 1, current + direction)))
    })

    // Lazy routes and data-driven controls can mount after navigation completes.
    useEffect(() => {
        if (!pending || pathname !== pending.route) return
        const check = () => {
            const target = document.querySelector(pending.target)
            if (!(target instanceof HTMLElement) || !target.getClientRects().length) return
            setReady(true)
            observer?.disconnect()
        }
        const observer = new MutationObserver(check)
        observer.observe(document.body, { childList: true, subtree: true, attributes: true })
        check()
        const timeout = window.setTimeout(() => {
            observer?.disconnect()
            const target = document.querySelector(pending.target)
            if (!(target instanceof HTMLElement) || !target.getClientRects().length) {
                setPending(null)
                setUnavailable(true)
            }
        }, 10000)
        return () => {
            observer?.disconnect()
            window.clearTimeout(timeout)
        }
    }, [pending, pathname])

    // Leaving the destination cancels the hint, including permission redirects.
    useEffect(() => {
        if (previousPathname.current !== pathname && pending && pathname !== pending.route) {
            setPending(null)
            setReady(false)
        }
        previousPathname.current = pathname
    }, [pathname, pending])

    const dismissAnnouncement = () => {
        if (historyScope && feature) markReleaseSeen(historyScope, feature.id)
        setShowPreview(false)
    }

    const tryFeature = () => {
        setShowPreview(false)
        setOpen(false)
        setReady(false)
        setUnavailable(false)
        setPending(feature)
        navigate(feature.route)
    }

    if (!feature)
        return <PageFeatureTour key={historyScope} historyScope={historyScope} enabled={!pending && !open} onActiveChange={handlePageTourActiveChange} />

    return (
        <>
            <PageFeatureTour key={historyScope} historyScope={historyScope} enabled={!pending && !open} onActiveChange={handlePageTourActiveChange} />
            {showPreview && !open && !pending && !pageTourActive && (
                <aside
                    {...swipeHandlers}
                    aria-label="New feature preview"
                    className="touch-pan-y fixed bottom-4 right-4 z-40 w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl bg-surface text-foreground shadow-overlay"
                >
                    <div className="relative overflow-hidden bg-accent px-3 pt-3">
                        <div className="mb-3 pr-8">
                            <p className="text-sm font-semibold text-accent-foreground">Hey there 👋</p>
                            <p className="mt-1 text-xs text-accent-foreground/80">Here’s what’s new in Numeo</p>
                        </div>
                        <img draggable={false} src={feature.image} alt="" className="h-24 w-full rounded-t-lg object-cover object-top" />
                        <Button
                            variant="secondary"
                            size="sm"
                            iconOnly
                            aria-label="Expand feature preview"
                            className="absolute right-2 top-2"
                            onClick={() => setOpen(true)}
                        >
                            <Maximize2 className="h-4 w-4" />
                        </Button>
                    </div>
                    <div className="p-4">
                        <h2 className="text-sm font-semibold">{feature.title}</h2>
                        <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-muted">{feature.description}</p>
                        <div className="mt-3 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1" aria-label="Release slides">
                                {announcements.map((item, slideIndex) => (
                                    <button
                                        key={item.id}
                                        type="button"
                                        aria-label={`Slide ${slideIndex + 1}: ${item.title}`}
                                        aria-current={slideIndex === index ? 'step' : undefined}
                                        className="flex h-8 w-6 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                        onClick={() => setIndex(slideIndex)}
                                    >
                                        <span className={`h-2 w-2 rounded-full ${slideIndex === index ? 'bg-accent' : 'bg-default'}`} />
                                    </button>
                                ))}
                            </div>
                            <Button size="sm" variant="ghost" onClick={dismissAnnouncement}>
                                Discard
                            </Button>
                            <Button size="sm" onClick={tryFeature}>
                                Try it <ArrowRight className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    </div>
                </aside>
            )}
            <Modal
                open={open}
                onClose={() => {
                    setOpen(false)
                    dismissAnnouncement()
                }}
                size="lg"
                showCloseButton={false}
                ariaLabel="What’s new in Numeo"
            >
                <div {...swipeHandlers} className="touch-pan-y text-foreground">
                    <div className="relative overflow-hidden rounded-2xl bg-accent px-12 pt-8 sm:px-14">
                        <button
                            type="button"
                            aria-label="Close release preview"
                            onClick={() => {
                                setOpen(false)
                                dismissAnnouncement()
                            }}
                            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-accent-foreground/80 transition-colors hover:bg-accent-hover hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-foreground"
                        >
                            <X className="h-4 w-4" />
                        </button>
                        <div className="mb-4 pr-6">
                            <p className="text-xl font-semibold text-accent-foreground">Hey there 👋</p>
                            <p className="mt-1 text-sm text-accent-foreground/80">Here’s what’s new in Numeo</p>
                        </div>
                        <button
                            type="button"
                            aria-label="Previous slide"
                            disabled={index === 0}
                            onClick={() => setIndex(index - 1)}
                            className="absolute left-2 top-1/2 mt-6 flex h-8 w-8 items-center justify-center rounded-full text-accent-foreground transition-colors hover:bg-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-foreground disabled:cursor-not-allowed disabled:opacity-35"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </button>
                        <button
                            type="button"
                            aria-label="Next slide"
                            disabled={index === announcements.length - 1}
                            onClick={() => setIndex(index + 1)}
                            className="absolute right-2 top-1/2 mt-6 flex h-8 w-8 items-center justify-center rounded-full text-accent-foreground transition-colors hover:bg-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-foreground disabled:cursor-not-allowed disabled:opacity-35"
                        >
                            <ChevronRight className="h-5 w-5" />
                        </button>
                        <img draggable={false} src={feature.image} alt="" className="h-48 w-full rounded-t-xl object-cover object-top shadow-surface sm:h-72" />
                    </div>
                    <div className="px-2 pb-2 pt-6 sm:px-6">
                        <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">{feature.title}</h2>
                        <p className="mt-3 min-h-20 text-base leading-relaxed text-foreground-muted">{feature.description}</p>
                        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                            <div className="flex items-center gap-2" aria-label="Release slides">
                                {announcements.map((item, slideIndex) => (
                                    <button
                                        key={item.id}
                                        type="button"
                                        aria-label={`Slide ${slideIndex + 1}: ${item.title}`}
                                        aria-current={slideIndex === index ? 'step' : undefined}
                                        className="flex h-8 w-6 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                                        onClick={() => setIndex(slideIndex)}
                                    >
                                        <span className={`h-2.5 w-2.5 rounded-full ${slideIndex === index ? 'bg-accent' : 'bg-default'}`} />
                                    </button>
                                ))}
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="ghost"
                                    onClick={() => {
                                        setOpen(false)
                                        dismissAnnouncement()
                                    }}
                                >
                                    Discard
                                </Button>
                                <Button onClick={tryFeature} suffix={<ArrowRight className="h-4 w-4" />}>
                                    Try it
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
            {pending && ready && pathname === pending.route && (
                <ReleaseTour
                    steps={[{ target: pending.target, title: pending.title, content: pending.hint, disableBeacon: true, placement: 'auto' }]}
                    onCallback={({ status }) => {
                        if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
                            markReleaseSeen(historyScope, pending.id)
                            setPending(null)
                            setReady(false)
                        }
                    }}
                />
            )}
            <Modal open={unavailable} onClose={() => setUnavailable(false)} ariaLabel="Feature preview unavailable">
                <h2 className="text-lg font-semibold text-foreground">This feature isn’t available here yet</h2>
                <p className="mt-2 text-sm text-foreground-muted">The preview control could not be found. Refresh the page to explore another feature.</p>
                <Button className="mt-4" onClick={() => setUnavailable(false)}>
                    Got it
                </Button>
            </Modal>
        </>
    )
}
